import React, { useState, useEffect } from "react";
import { Button, Form, FormGroup, Input, Label, Alert, Spinner } from "reactstrap";
import axios from "axios";
import Select, { createFilter } from "react-select";
import { API_URL_partidas_curso } from "../../../constants";

import { getAuthHeaders } from "../../../utils/auth";
const ProyectoCursoForm = ({
  proyectoCode,
  proyectoName,
  proyectoMoneda,
  partida_curso = null,
  resetState,
  toggle
}) => {
  const [formData, setFormData] = useState({
    pk: 0,
    partida_curso: "",
    nivel: "",
    cantidad: "",
    costo: "",
    referencia: "",
    moneda: "",
    tipo_cambio: null,
    proyecto_code: ""
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Set project code
    setFormData(prev => ({
      ...prev,
      proyecto_code: proyectoCode
    }));

    // If editing, load partida_curso data
    if (partida_curso) {
      const {
        pk,
        partida_curso: partidaCursoValue,
        nivel,
        cantidad,
        costo,
        referencia,
        moneda,
        tipo_cambio,
        proyecto_code,
      } = partida_curso;

      setFormData({
        pk: pk || 0,
        partida_curso: partidaCursoValue || "",
        nivel: nivel || "",
        cantidad: cantidad || "",
        costo: costo || "",
        referencia: referencia || "",
        moneda: moneda || "",
        tipo_cambio: tipo_cambio ?? null,
        proyecto_code: proyecto_code || proyectoCode
      });
    }
  }, [proyectoCode, partida_curso]);

  const validateForm = () => {
    const errors = {};
    const { cantidad, costo, moneda } = formData;

    if (!cantidad || cantidad === '') {
      errors.cantidad = 'Por favor ingresa el número de estudiantes';
    } else if (parseFloat(cantidad) <= 0) {
      errors.cantidad = 'El número de estudiantes debe ser mayor a 0';
    }

    if (!costo || costo === '') {
      errors.costo = 'Por favor ingresa el precio por estudiante';
    } else if (parseFloat(costo) < 0) {
      errors.costo = 'El precio no puede ser negativo';
    }

    if (!moneda || moneda === '') {
      errors.moneda = 'Por favor selecciona una moneda';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const onChangeNumber = (e) => {
    const { name, value } = e.target;

    // Allow empty value or valid decimal number
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      setErrorMessage('');
      
      // Clear validation error for this field
      if (validationErrors[name]) {
        setValidationErrors(prev => ({
          ...prev,
          [name]: null
        }));
      }
    } else {
      setErrorMessage('Invalid decimal number format');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setErrorMessage('Por favor completa todos los campos obligatorios marcados con *');
      setTimeout(() => {
        const firstError = document.querySelector('.is-invalid-custom');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = firstError.querySelector('input, select');
          if (input) input.focus();
        }
      }, 100);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const headers = getAuthHeaders();

    // The Curso model has referencia as TextField(null=True) — no blank=True,
    // so DRF rejects "" with a "field is required" validation error. Send
    // null instead when the user left it blank.
    const payload = {
      ...formData,
      referencia: formData.referencia === "" ? null : formData.referencia,
    };

    try {
      if (partida_curso) {
        const url = `${API_URL_partidas_curso}${formData.pk}/`;
        await axios.put(url, payload, { headers });
      } else {
        await axios.post(API_URL_partidas_curso, payload, { headers });
      }

      resetState();
      toggle();
    } catch (error) {
      console.error("Error saving partida del curso:", error);

      // Handle server-side validation errors
      if (error.response?.data) {
        const serverErrors = error.response.data;
        if (typeof serverErrors === 'object' && !Array.isArray(serverErrors)) {
          setValidationErrors(serverErrors);
          setErrorMessage('Error de validación. Por favor revisa los campos.');
        } else {
          setErrorMessage(partida_curso ? 'Error al actualizar. Por favor verifica los datos.' : 'Error al guardar. Por favor verifica los datos.');
        }
      } else {
        setErrorMessage('Error de conexión. Por favor intenta nuevamente.');
      }
      setIsSubmitting(false);
    }
  };

  const defaultIfEmpty = (value) => {
    return value === "" ? "" : value;
  };

  const h3Style = { color: 'blue' };

  // Filter option for react-select (search without accents)
  const filterOption = createFilter({
    ignoreAccents: true,
    ignoreCase: true,
    trim: true,
    matchFrom: "any",
  });

  // Options for Select components
  const monedaOptions = [
    { value: "MXN", label: "MXN" },
    { value: "USD", label: "USD" },
    { value: "EUR", label: "EUR" },
  ];

  const fxRowCurrency = formData.moneda || "USD";
  const fxProjectCurrency = proyectoMoneda || "USD";
  const fxCurrency = fxRowCurrency !== "USD" ? fxRowCurrency : fxProjectCurrency;
  const showTipoCambio = fxRowCurrency !== "USD" || fxProjectCurrency !== "USD";

  return (
    <div>
      <h3>{proyectoName}</h3>
      {proyectoCode && <h3 style={h3Style}>{proyectoCode.toUpperCase()}</h3>}
      
      <div className="form-requirements-note">
        <strong>Nota:</strong> Los campos marcados con <span className="required-asterisk">*</span> son obligatorios.
      </div>
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label for="nivel">Nivel:</Label>
          <Input
            type="text"
            name="nivel"
            id="nivel"
            onChange={onChange}
            value={defaultIfEmpty(formData.nivel)}
            placeholder="Ej: WB online, YB online, GB online, BB online"
          />
        </FormGroup>

        <FormGroup>
          <Label for="cantidad">
            Número de estudiantes<span className="required-asterisk">*</span>
          </Label>
          <Input
            type="number"
            name="cantidad"
            onChange={onChange}
            value={defaultIfEmpty(formData.cantidad)}
            inputMode="numeric"
            onWheel={(e) => e.target.blur()}
            className={validationErrors.cantidad ? 'is-invalid-custom' : ''}
            step="1"
          />
          {validationErrors.cantidad && (
            <div className="invalid-feedback-custom">{validationErrors.cantidad}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="costo">
            Precio x estudiante<span className="required-asterisk">*</span>
          </Label>
          <Input
            type="text"
            name="costo"
            onChange={onChangeNumber}
            value={defaultIfEmpty(formData.costo)}
            inputMode="numeric"
            onWheel={(e) => e.target.blur()}
            className={validationErrors.costo ? 'is-invalid-custom' : ''}
            placeholder="0.00"
          />
          {validationErrors.costo && (
            <div className="invalid-feedback-custom">{validationErrors.costo}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="moneda">
            Moneda<span className="required-asterisk">*</span>
          </Label>
          <Select
            options={monedaOptions}
            value={monedaOptions.find((o) => o.value === formData.moneda) || null}
            onChange={(opt) => {
              setFormData(prev => ({ ...prev, moneda: opt ? opt.value : "" }));
              setValidationErrors(prev => ({ ...prev, moneda: null }));
            }}
            placeholder="Selecciona moneda"
            isClearable
            filterOption={filterOption}
            className={validationErrors.moneda ? 'is-invalid' : ''}
          />
          {validationErrors.moneda && (
            <div className="invalid-feedback-custom">{validationErrors.moneda}</div>
          )}
        </FormGroup>

        {showTipoCambio && (
          <FormGroup>
            <Label for="tipo_cambio">
              Tipo de Cambio ({fxCurrency} por 1 USD):
            </Label>
            <Input
              type="number"
              step="0.0001"
              min="0"
              name="tipo_cambio"
              placeholder="ej. 17 (vacío para usar la tasa mensual)"
              onChange={(e) => setFormData(prev => ({
                ...prev,
                tipo_cambio: e.target.value === "" ? null : Number(e.target.value),
              }))}
              value={formData.tipo_cambio ?? ""}
            />
            <small className="form-text text-muted">
              {fxRowCurrency !== "USD"
                ? "Opcional. Si lo capturas, esta partida se convierte a USD dividiendo entre este número. Vacío = tasa del mes de inicio del proyecto."
                : `Opcional. Si lo capturas, esta partida en USD se convierte a ${fxProjectCurrency} multiplicando por este número. Vacío = tasa del mes de inicio del proyecto.`}
            </small>
          </FormGroup>
        )}

        <FormGroup>
          <Label for="referencia">Referencia:</Label>
          <Input
            type="text"
            name="referencia"
            onChange={onChange}
            value={defaultIfEmpty(formData.referencia)}
          />
        </FormGroup>
        
        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
        
        <Button color="primary" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" /> Guardando...
            </>
          ) : (
            'Guardar'
          )}
        </Button>
      </Form>
    </div>
  );
};

export default ProyectoCursoForm;